/* Each section owns a native sticky stage. Page scroll is never intercepted. */
window.Eurus = window.Eurus || {};
if (!window.Eurus.stackedStory) {
  class StackedStory {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-mss-track]');
      this.stage = root.querySelector('[data-mss-stage]');
      this.content = root.querySelector('.mss__content') || this.stage;
      this.cards = [...root.querySelectorAll('[data-mss-card]')];
      this.bodies = this.cards.map(card => card.querySelector('[data-mss-body]'));
      this.arrivals = new Float64Array(this.cards.length);
      this.scales = new Float64Array(this.cards.length);
      this.transforms = new Array(this.cards.length);
      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.interactive = false;
      this.active = 0;
      this.visible = true;
      this.headerHeight = null;
      this.dead = false;
      this.frame = 0;
      this.needsMeasure = false;
      this.lastProgress = null;
      this.renderedActive = null;
      const options = { signal: this.abort.signal };
      window.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      window.addEventListener('resize', () => this.queueMeasure(), options);
      window.visualViewport?.addEventListener('resize', () => this.queueMeasure(), options);
      this.motion.addEventListener('change', () => this.queueMeasure(), options);
      document.addEventListener('eurus:sticky-header-moved', event => {
        const height = Number(event.detail?.heightHeader);
        if (Number.isFinite(height) && height !== this.headerHeight) {
          this.headerHeight = Math.max(0, height);
          this.queueMeasure();
        }
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
        if (index < 0 || (event.detail.sectionId && event.detail.sectionId !== root.dataset.sectionId)) return;
        if (this.interactive) this.showCard(index);
        else this.cards[index].scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }, options);
      document.addEventListener('shopify:section:load', () => this.queueMeasure(), options);
      document.addEventListener('shopify:section:reorder', () => this.queueMeasure(), options);
      root.addEventListener('focusin', event => {
        if (!this.interactive) return;
        const index = this.cards.findIndex(card => card.contains(event.target));
        if (index >= 0) this.showCard(index);
      }, options);
      root.querySelectorAll('img').forEach(img => {
        img.addEventListener('load', () => this.queueMeasure(), options);
        img.addEventListener('error', () => this.queueMeasure(), options);
      });
      this.resize = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width !== this.observedWidth) {
          this.observedWidth = width;
          this.queueMeasure();
        }
      });
      this.resize.observe(this.content);
      this.copyResize = new ResizeObserver(() => this.queueMeasure());
      root.querySelectorAll('.mss__copy').forEach(copy => this.copyResize.observe(copy));
      this.headerResize = new ResizeObserver(() => this.queueMeasure());
      document.querySelectorAll('.section-header, #x-announcement').forEach(el => this.headerResize.observe(el));
      this.intersection = new IntersectionObserver(entries => {
        if (this.dead) return;
        this.visible = entries[0].isIntersecting;
        this.syncPromotion();
        if (this.visible) this.queueMeasure();
        else if (!this.needsMeasure) {
          cancelAnimationFrame(this.frame);
          this.frame = 0;
        }
      }, { rootMargin: '200px' });
      this.intersection.observe(this.track);
      document.fonts?.ready.then(() => this.queueMeasure());
      this.measure();
    }
    queueMeasure() {
      if (this.dead) return;
      this.needsMeasure = true;
      this.schedule(true);
    }
    syncPromotion() {
      this.root.classList.toggle('mss--near', this.interactive && this.visible && !this.dead);
    }
    measuredHeader() {
      if (this.headerHeight !== null) return this.headerHeight;
      const store = window.Alpine?.store?.('xHeaderMenu');
      if (store?.initialized) {
        return Math.max(0, (store.isSticky ? store.stickyOffsetHeight - (store.toolbarOffsetHeight || 0) : 0) + (store.announcementOffsetHeight || 0));
      }
      let bottom = 0;
      document.querySelectorAll('.section-header.is-sticky, .section-header .is-sticky, #x-announcement[data-is-sticky="true"]').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 1 && rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
      });
      return bottom;
    }
    clearCards() {
      this.lastProgress = null;
      this.renderedActive = null;
      this.transforms.fill(null);
      this.root.classList.remove('mss--near');
      this.cards.forEach((card, i) => {
        card.style.removeProperty('transform');
        card.style.removeProperty('clip-path');
        this.bodies[i].style.removeProperty('opacity');
        this.bodies[i].style.removeProperty('pointer-events');
        card.style.removeProperty('z-index');
        this.bodies[i].inert = false;
        this.bodies[i].removeAttribute('aria-hidden');
      });
    }
    clearFit() {
      this.root.style.removeProperty('--mss-fit-width');
      this.root.classList.remove('mss--compact-desktop');
    }
    fitDesktop() {
      // Reserve the exposed strips and a rounding allowance; keep the selected ratio.
      const height = this.available - (this.cards.length - 1) * this.layer - 4;
      const [numerator, denominator = 1] = getComputedStyle(this.stage).getPropertyValue('--mss-ratio').split('/').map(Number);
      const ratio = numerator / denominator;
      if (height <= 0 || !Number.isFinite(ratio) || ratio <= 0) return;
      const width = Math.min(this.width, Math.floor(height * ratio));
      if (width <= 0) return;
      this.root.style.setProperty('--mss-fit-width', width + 'px');
      this.width = this.stage.getBoundingClientRect().width;
      this.cardDimensions();
      if (this.stageHeight > this.available) {
        this.root.classList.add('mss--compact-desktop');
        this.cardDimensions();
      }
    }
    cardDimensions() {
      this.cardHeight = Math.ceil(Math.max(0, ...this.cards.map(card => card.getBoundingClientRect().height)));
      this.stageHeight = this.cardHeight + Math.max(0, this.cards.length - 1) * this.layer;
    }
    fullHeight(reserveStrips) {
      const strips = reserveStrips ? Math.max(0, this.cards.length - 1) * this.layer : 0;
      this.root.style.setProperty('--mss-full-height', Math.max(0, this.available - strips - 4) + 'px');
    }
    measure() {
      if (this.dead) return;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.needsMeasure = false;
      this.interactive = false;
      this.root.classList.remove('mss--interactive');
      this.clearCards();
      this.clearFit();
      this.root.style.removeProperty('--mss-full-height');
      const style = getComputedStyle(this.stage);
      this.layer = parseFloat(style.getPropertyValue('--mss-layer')) || 16;
      this.gap = parseFloat(style.getPropertyValue('--mss-gap-current')) || 0;
      this.width = this.stage.getBoundingClientRect().width;
      const mobile = window.innerWidth < 768;
      const fullscreen = (mobile ? this.root.dataset.ratioMobile : this.root.dataset.ratio) === 'fullscreen';
      const clearance = mobile ? this.root.dataset.clearanceMobile : this.root.dataset.clearance;
      this.top = this.measuredHeader() + Number(clearance ?? this.root.dataset.clearance ?? 12);
      const viewport = Math.min(window.innerHeight, window.visualViewport?.height || window.innerHeight);
      this.available = Math.max(0, viewport - this.top - 24);
      const mobileStatic = window.innerWidth < 768 && this.root.dataset.mobile === 'static';
      const eligible = this.cards.length > 1 && this.root.dataset.sticky === 'true' && !mobileStatic && !this.motion.matches && this.width > 0;
      if (fullscreen) this.fullHeight(eligible);
      this.cardDimensions();
      if (eligible && !mobile && this.stageHeight > this.available) {
        if (fullscreen) {
          this.root.classList.add('mss--compact-desktop');
          this.cardDimensions();
        } else this.fitDesktop();
      }
      this.interactive = eligible && this.stageHeight <= this.available;
      if (!this.interactive) {
        this.clearFit();
        if (fullscreen) this.fullHeight(false);
        this.width = this.stage.getBoundingClientRect().width;
        this.cardDimensions();
        this.root.dataset.mode = 'static';
        return;
      }
      this.hold = this.cardHeight * .4 * Number(this.root.dataset.distance || 100) / 100;
      this.step = this.cardHeight + this.gap + this.hold;
      this.travel = this.cardHeight + this.gap - this.layer;
      this.distance = (this.cards.length - 1) * (this.step - this.layer) + this.hold;
      this.root.style.setProperty('--mss-card-height', this.cardHeight + 'px');
      this.root.style.setProperty('--mss-stage-height', this.stageHeight + 'px');
      this.root.style.setProperty('--mss-track-height', this.stageHeight + this.distance + 'px');
      this.root.style.setProperty('--mss-sticky-top', this.top + 'px');
      this.root.classList.add('mss--interactive');
      this.cards.forEach((card, i) => { card.style.zIndex = String(i + 1); });
      this.syncPromotion();
      this.root.dataset.mode = 'stack';
      this.start = this.track.getBoundingClientRect().top + window.scrollY - this.top;
      this.update();
    }
    schedule(force = false) {
      if (this.dead || this.frame || (!force && (!this.interactive || !this.visible))) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsMeasure) this.measure();
        else if (this.visible) this.update();
      });
    }
    update() {
      if (!this.interactive || this.dead) return;
      const progress = Math.max(0, Math.min(this.distance, window.scrollY - this.start));
      if (progress === this.lastProgress) return;
      this.lastProgress = progress;
      let active = 0;
      let depth = 0;
      // Reuse buffers and accumulate depth once, rather than slicing per card.
      for (let i = this.cards.length - 1; i >= 0; i--) {
        const entry = i * (this.step - this.layer) - this.travel;
        const t = i ? Math.max(0, Math.min(1, (progress - entry) / this.travel)) : 1;
        // Smootherstep has zero first and second derivatives at both ends.
        const arrival = t * t * t * (t * (t * 6 - 15) + 10);
        this.arrivals[i] = arrival;
        this.scales[i] = Math.max(.82, 1 - depth * .03);
        depth += arrival;
      }
      let offset = 0;
      for (let i = 0; i < this.cards.length; i++) {
        const pin = i * this.layer;
        const travel = i * this.step - progress;
        const scale = this.scales[i];
        // Blend scale-aware strip offsets into the original arrival timeline.
        const y = Math.max(pin, travel) + this.arrivals[i] * (offset - pin);
        const transform = 'translate3d(0,' + y + 'px,0) scale(' + scale + ')';
        if (transform !== this.transforms[i]) {
          this.cards[i].style.transform = transform;
          this.transforms[i] = transform;
        }
        offset += this.layer * scale;
        if (i > 0 && travel <= pin + .5) active = i;
      }
      if (active !== this.renderedActive) {
        this.bodies.forEach((body, i) => {
          const covered = i < active;
          body.style.opacity = covered ? '0' : '1';
          body.style.pointerEvents = covered ? 'none' : '';
        });
        this.root.dataset.active = String(active);
        this.renderedActive = active;
      }
      this.active = active;
      this.root.dataset.progress = String(progress / this.distance);
    }
    showCard(index) {
      if (!this.interactive || this.dead) return;
      const pinScroll = this.start + index * (this.step - this.layer) + 1;
      window.scrollTo({ top: pinScroll, behavior: 'instant' });
      this.update();
    }
    destroy() {
      if (this.dead) return;
      this.dead = true;
      this.abort.abort();
      this.resize.disconnect();
      this.copyResize.disconnect();
      this.headerResize.disconnect();
      this.intersection.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.needsMeasure = false;
      this.root.classList.remove('mss--interactive');
      this.clearFit();
      this.root.style.removeProperty('--mss-full-height');
      this.clearCards();
      delete this.root.__stackedStory;
    }
  }
  const init = root => {
    if (root && !root.__stackedStory) root.__stackedStory = new StackedStory(root);
  };
  const initAll = scope => {
    if (!scope) return;
    if (scope.matches?.('.mss')) init(scope);
    scope.querySelectorAll?.('.mss').forEach(init);
  };
  window.Eurus.stackedStory = { initAll };
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('.mss')) event.target.__stackedStory?.destroy();
    event.target.querySelectorAll?.('.mss').forEach(root => root.__stackedStory?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
} else {
  window.Eurus.stackedStory.initAll(document);
}
